import { runCommand } from '../appwrite/command-gateway';
import { getSessionId, getVisitorId } from './activity-ids';

const UTM_CACHE_KEY = 'rs-visit-utm';

interface Utm {
  source?: string;
  medium?: string;
  campaign?: string;
}

/**
 * Los parámetros `utm_*` solo llegan en la URL de la primera página que se
 * visita en la sesión; se guardan una vez para que los eventos posteriores
 * de la misma visita sigan sabiendo de dónde vino.
 */
function utmForSession(): Utm {
  try {
    const cached = sessionStorage.getItem(UTM_CACHE_KEY);
    if (cached) return JSON.parse(cached) as Utm;
  } catch {
    // sigue con los parámetros de la URL actual si no se pudo leer el caché
  }

  const params = new URLSearchParams(window.location.search);
  const utm: Utm = {
    source: params.get('utm_source') || undefined,
    medium: params.get('utm_medium') || undefined,
    campaign: params.get('utm_campaign') || undefined,
  };
  try {
    if (utm.source || utm.medium || utm.campaign) {
      sessionStorage.setItem(UTM_CACHE_KEY, JSON.stringify(utm));
    }
  } catch {
    // sin almacenamiento disponible: el resto del evento se manda igual
  }
  return utm;
}

/** localhost y *.localhost (`app.localhost`, etc.). */
function isLocalDevelopment(hostname: string): boolean {
  const host = hostname.toLowerCase();
  return host === 'localhost' || host === '127.0.0.1' || host.endsWith('.localhost');
}

/**
 * Registra un evento del Tracker de actividad. Nunca espera respuesta ni
 * propaga errores: el tracker no debe interrumpir ni enlentecer lo que la
 * persona está haciendo de verdad.
 */
export function track(action: string, properties?: Record<string, unknown>): void {
  // Las pruebas no deben golpear la red real: `MODE` es 'test' bajo Vitest.
  if (import.meta.env.MODE === 'test') return;
  // Tampoco navegar en local mientras se desarrolla: no es actividad real.
  if (isLocalDevelopment(window.location.hostname)) return;
  void runCommand('track_event', {
    // No se llama "action": ese campo ya lo usa el sobre de la Function para
    // elegir el handler ("track_event" siempre, para esta llamada).
    eventName: action,
    visitorId: getVisitorId(),
    sessionId: getSessionId(),
    properties,
    path: window.location.pathname,
    surface: 'role',
    referrer: document.referrer || undefined,
    utm: utmForSession(),
  }).catch(() => {
    // silencioso a propósito
  });
}
